<script lang="ts">
  import Text from '../../components/text.svelte';
  import Icon from '../../icon/icon.svelte';
  import DieElement from '../../dice-tray/die.svelte';
  import { createDie } from '../../models/die.type';
  import { glossary } from '../../../store';
  import type { itemDefinition } from '../../models/item.type';
  import type { Die as DieObject, DieCreation } from '../../models/die.type';
  import type { Technique } from '../../models/techniques.type';

  interface Props {
    // `category` is stamped on in glossary.svelte, which is the only place that
    // still knows which of the four item files a definition came from.
    data: itemDefinition & { category?: string };
  }

  let { data }: Props = $props();

  /**
   * An Item is a Modifier, so what an item actually *does* lives in the hooks
   * its class implements -- every one of the 15 either grants a technique you
   * can spend from the inventory, or grants a die. The definition's own `text`
   * is flavour, so on its own the entry never said what the item was for.
   *
   * The hooks are arbitrary code, so they are called defensively: none of the
   * current 15 touch `this.owner`, which an instance built here does not have,
   * but nothing stops a future one from doing so.
   */
  let grantedTechniques: Array<Technique> = $state.raw([]);
  let grantedDice: Array<{ die: DieObject; creation: DieCreation }> = $state.raw([]);

  if (data?.class) {
    const instance = new data.class(data);

    if (typeof instance.getTechniques === 'function') {
      try {
        grantedTechniques = instance.getTechniques([]) ?? [];
      } catch {
        grantedTechniques = [];
      }
    }

    if (typeof instance.getDice === 'function') {
      try {
        const creations = instance.getDice([]) ?? [];
        grantedDice = creations.map((creation, index) => ({
          creation,
          die: createDie(creation, index, `${data.type}-${index}`),
        }));
      } catch {
        grantedDice = [];
      }
    }
  }

  function openDie(type: string) {
    glossary.selected.set('die');
    glossary.searchTerm.set(type);
  }

  function levelLabel(level: number | undefined) {
    if (!level) {
      return 'lvl 0';
    }
    return level > 0 ? `lvl +${level}` : `lvl ${level}`;
  }
</script>

<div class="container entry">
  <div class="icon">
    <Icon name={data.icon} />
    {#if data.bonusIcon}
      <Icon name={data.bonusIcon} class="bonus" />
    {/if}
  </div>

  <div class="information">
    <div class="heading">
      <h2>{data.title}</h2>

      {#if data.category}
        <span class="category">{data.category}</span>
      {/if}
    </div>

    <Text data={data.text} class="text" />

    {#if grantedTechniques.length || grantedDice.length}
      <div class="grants">
        <span class="grants-label">Grants</span>

        {#each grantedDice as granted (granted.die.id)}
          <button class="grant die" onclick={() => openDie(granted.creation.type)}>
            <div class="die-container">
              <DieElement die={granted.die} die3D={false} hover={true} sizeMod={1} />
            </div>

            <span class="grant-body">
              <span class="grant-title">{granted.creation.type}</span>
              <span class="grant-text">{levelLabel(granted.creation.level)}</span>
            </span>
          </button>
        {/each}

        {#each grantedTechniques as technique, index (index)}
          <div class="grant">
            <span class="grant-body">
              <span class="grant-title">{technique.title}</span>
              <span class="grant-text">{technique.text}</span>

              {#if technique.requirements?.length}
                {#each technique.requirements as requirement, reqIndex (reqIndex)}
                  {#if requirement.text}
                    <span class="grant-requirement">{requirement.text}</span>
                  {/if}
                {/each}
              {/if}
            </span>
          </div>
        {/each}
      </div>
    {/if}
  </div>
</div>

<style lang="less">
  @import '../../../defines.less';

  .container {
    display: grid;
    // The third column used to be a fixed 100px reserved for a "View Code"
    // button that is commented out, so every entry carried a dead gap.
    grid-template-columns: auto minmax(0, 1fr);
    grid-gap: 14px;
    align-items: start;

    text-align: left;
  }

  // Framed like a portrait so an icon-led row and a portrait-led row read as
  // the same kind of thing now that neither sits in a card of its own.
  .icon {
    .glossary-plate(64px);
    --icon-size: 38px;

    @media @mobile {
      .glossary-plate(50px);
      --icon-size: 30px;
    }

    // Pinned over the corner of the frame, the way a badge sits on one.
    :global(.bonus) {
      position: absolute;
      z-index: 2;
      top: -6px;
      left: -6px;
      --icon-size: 26px;
      background: rgba(0, 0, 0, 0.6);
      border-radius: 999px;
      padding: 2px;

      @media @mobile {
        --icon-size: 20px;
      }
    }
  }

  .information {
    display: flex;
    flex-direction: column;
    grid-gap: 6px;
    min-width: 0;
  }

  .heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    // Left-packed, not space-between: without a card edge to push against, a
    // right-aligned chip just floats off on its own halfway across the page.
    justify-content: flex-start;
    grid-gap: 8px;

    h2 {
      font-size: 1.15rem;
      text-transform: capitalize;
    }
  }

  .category {
    padding: 2px 9px;
    font-size: 0.6rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    opacity: 0.7;
    background: var(--background-colour-dark);
    border: 1px solid var(--background-colour-light);
    border-radius: 999px;
  }

  .container :global(.text) {
    font-size: 0.9rem;
    opacity: 0.85;
  }

  .grants {
    display: flex;
    flex-direction: column;
    grid-gap: 4px;
    margin-top: 2px;
  }

  .grants-label {
    font-size: 0.6rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: 0.5;
  }

  // Indented off a rule rather than boxed: a grant is a sub-clause of the
  // entry above it, and drawing a box round it put the cards back one level
  // down.
  .grant {
    display: flex;
    // button.less centres its content, which left a lone die stranded in the
    // middle of a full-width row.
    justify-content: flex-start;
    align-items: center;
    grid-gap: 10px;
    width: 100%;
    padding: 6px 0 6px 12px;
    text-align: left;
    background: none;
    border: none;
    border-left: 2px solid var(--background-colour-light);
    border-radius: 0;
    box-shadow: none;

    // The die grants are buttons through to the Die section; the technique
    // grants are not, so the pill reset only matters for the former.
    &.die {
      cursor: pointer;
      transition:
        border-color 0.2s,
        color 0.2s;

      &:hover,
      &:focus-visible {
        filter: none;
        color: @edge-shine;
        border-left-color: @edge-warm;
      }

      &:active {
        box-shadow: none;
      }
    }

    .die-container {
      position: relative;
      flex: 0 0 auto;
    }

    .grant-body {
      display: flex;
      flex-direction: column;
      grid-gap: 2px;
      min-width: 0;
    }

    .grant-title {
      font-size: 0.9rem;
      font-weight: 700;
      text-transform: capitalize;
    }

    .grant-text {
      font-size: 0.85rem;
      opacity: 0.8;
    }

    .grant-requirement {
      font-size: 0.75rem;
      opacity: 0.6;

      &::before {
        content: '\26A0\00a0';
      }
    }
  }
</style>
